import React, { FC, useEffect, useMemo, useState } from 'react'
import { View, useWindowDimensions, ScrollView, Pressable, KeyboardAvoidingView, Platform, Image } from 'react-native'
import { useIsFocused } from '@react-navigation/native'
import { ThemeConsumer } from '@rneui/themed';
import { AxiosError } from 'axios'
import EIcon from "react-native-vector-icons/Entypo";
import Toast from 'react-native-toast-message';

import { config } from '../../shared/environment'
import { appColors, appFonts, fontSize } from '../../themes/appTheme'
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { useAppNavigation } from '../../types/@react-navigation/navigation';
import { useAppRoute } from '../../types/@react-navigation/navigation';

// Common component
import CommonHeader from '../../shared/commonComponents/commonHeader'
import CommonService from '../../shared/services/commonService'
import { productDetailsStyle } from '../../themes/components/sellScreen/productDetailsStyle'

import { addCartProducts, updateCartProducts } from '../../redux/slices/sellScreenSlice';
import { getOnePosProduct, getOnePosProductDiscount, getAuthorizedCartProduct } from '../../redux/slices/productDetailsSlice';

import { renderImageType, variantImage, optionValueType, buyXgetYDiscountType, cartPayload, automaticDiscountType, updateCartPayload, productDataType } from './productDetailsModule';

import { ZenCheckBox, ZenBadge, ZenText, ZenLoader, ZenButton, ZenToast, ZenInput } from 'zen-core-elements';

const ProductDetails: FC = () => {
    const selectedCustomer = useAppSelector((state) => state?.sellScreen.selectedCustomer)
    const cartProducts = useAppSelector((state) => state?.sellScreen.cartProductsForDetails)
    const locationValue = useAppSelector((state) => state?.common?.currentLocation)
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    const { params } = useAppRoute("ProductDetails")
    // Variable which is used to render styles based on device dimention
    const { width } = useWindowDimensions()
    // Variable used to store Focused state
    const isFocused = useIsFocused()
    // Variable which is used to navigation
    const navigation = useAppNavigation()
    // Variable which is used to store product weight
    const [options, setOptions] = useState<optionValueType[]>([])
    // Variable which is used to store notes about product
    const [notes, setNotes] = useState<string>('')
    // Variabl which is used to handle loader
    const [load, setLoad] = useState<boolean>(false)
    // Variable which is used to store product details
    const [productDetails, setProductDetails] = useState<any>()
    // Variable which is used to store cart count value
    const [count, setCount] = useState<number>(0)
    // Variable which is used to store variant image
    const [variantImage, setVariantImage] = useState<variantImage[]>([])
    // Variable which is used to display variant image
    const [displayImage, setDisplayImage] = useState<string>()
    // Variable which is used to store buyXgetY and automatic discount
    const [combinedDiscounts, setCombinedDiscounts] = useState<any>()
    // Variable which is used to store selected option id 
    const [selectedOption, setSelectedOption] = useState<optionValueType["productVariantMappings"][0]["variantCombinationDetail"] | null>(null)
    // Variable which is used for update cart loader
    const [updateCartLoader, setUpdateCartLoader] = useState<boolean>(false)
    // redux selector
    const industryName = useAppSelector((state) => state?.common?.industryDetails)
    // redux dispatch
    const dispatch = useAppDispatch()

    // useEffect hook which is used to call getProductDetails function when page is focused
    useEffect(() => {
        if (isFocused) {
            setLoad(true)
            getOnePosProductForSellScreen()
        }
    }, [isFocused])

    const VarientId = useMemo(() => {
        return selectedOption?.id || productDetails?.variantCombinationDetails[0]?.id
    }, [selectedOption?.id, productDetails])

    useEffect(() => {
        if (selectedOption) {
            let currentCartProduct = cartProducts.get(VarientId)
            if (currentCartProduct) {
                setCount(currentCartProduct?.quantity)
                setNotes(currentCartProduct?.productNotes)
            } else {
                setCount(selectedOption?.weightCount)
                setNotes("")
            }
            getAvailableOffers(selectedOption?.id)
        }
    }, [selectedOption, cartProducts, productDetails])

    const getCartProductVarient = (products: productDataType) => {
        if (products?.isVariants) {
            let ids: number[] = []
            products?.variantImage?.forEach((item) => {
                ids.push(item?.variantId)
            })
            for (let id of ids) {
                if (cartProducts?.has(id)) {
                    return cartProducts.get(id)
                }
            }
        } else {
            let id = products?.variantCombinationDetails[0]?.id
            return cartProducts.get(id)
        }
    }

    // Method which is used to get product details
    const getOnePosProductForSellScreen = async () => {
        const data = {
            locationId: locationValue?.id
        }
        await dispatch(getOnePosProduct({ storeId: config?.storeId, productId: params?.productId, locationId: data })).unwrap().then((productDetailsRes) => {
            const currentCartProduct = getCartProductVarient(productDetailsRes?.productData)
            if (productDetailsRes && productDetailsRes?.productData) {
                setProductDetails(productDetailsRes?.productData)
                setOptions(productDetailsRes?.productData?.productOptionMappings?.[0]?.option?.optionValues)
                //condition to check cart product and varient product
                if (currentCartProduct) {
                    if (productDetailsRes?.productData?.productOptionMappings?.length) {
                        let currentOption = productDetailsRes?.productData?.productOptionMappings[0]?.option?.optionValues?.filter((item) => item?.productVariantMappings[0]?.variantCombinationDetail?.id === currentCartProduct?.varientId)?.[0]?.productVariantMappings[0]?.variantCombinationDetail
                        setSelectedOption(currentOption)
                    } else {
                        setNotes(currentCartProduct?.productNotes)
                        setCount(currentCartProduct?.quantity)
                    }
                } else {
                    if (productDetailsRes?.productData?.productOptionMappings?.length) {
                        let initialOption = productDetailsRes?.productData?.productOptionMappings[0]?.option?.optionValues[0]?.productVariantMappings[0]?.variantCombinationDetail
                        setSelectedOption(initialOption)
                    } else {
                        setCount(productDetailsRes?.productData?.variantCombinationDetails[0]?.weightCount)
                    }
                }

                setVariantImage(productDetailsRes?.productData?.variantImage)
                let variantId = (productDetailsRes?.productData?.variantCombinationDetails[0]?.id)
                if (!productDetailsRes?.productData?.isVariants) {
                    getAvailableOffers(variantId)
                }
            }
        }).catch((Err: AxiosError) => {
            console.log("Error in get product details for sellscreen", Err)
        })
    }

    const getAvailableOffers = (variantId: number) => {
        dispatch(getOnePosProductDiscount({ storeId: config?.storeId, productId: params?.productId, variantId, locationId: { locationId: locationValue?.id } })).unwrap().then((discountRes) => {
            if (discountRes?.productDiscount) {
                setCombinedDiscounts([...(Array.isArray(discountRes?.productDiscount?.buyXgetYDiscount) ? discountRes.productDiscount.buyXgetYDiscount.map((item: buyXgetYDiscountType) => ({ ...item, type: 'BuyXgetY' })) : []), ...(Array.isArray(discountRes?.productDiscount?.automaticDiscount) ? discountRes.productDiscount.automaticDiscount.map((item: automaticDiscountType) => ({ ...item, type: 'Automatic' })) : [])]);
                setLoad(false)
            }
        }).catch((Err: AxiosError) => {
            console.log("Error in get discount type", Err)
        })
    }

    // Method which is used to render combination of variant image
    const renderImage = (val: renderImageType) => {
        variantImage?.map((item: variantImage) => {
            item?.images?.length > 0 && item?.images?.map((ele: renderImageType) => {
                if (val?.id == ele?.id) {
                    setDisplayImage(val?.fileUrl)
                }
            })
        })
    }

    // Method which is used to claim discount offer
    const claimOffer = async (value: any) => {
        const CustomerId = 3982;
        const payload: cartPayload = {
            storeId: config?.storeId,
            locationId: locationValue?.id,
            timeZone: "Asia / Calcutta",
            industryName: industryName?.name,
            discountDetails: value?.type == "BuyXgetY" ? { "automatic": [], "buyXgetY": { "discountId": value?.getdata?.id, "isCategory": false } } : { "automatic": { "discountId": value?.categoryid, "isCategory": false }, "buyXgetY": [] },
            isCart: true,
            currentDate: new Date(),
        }
        await dispatch(getAuthorizedCartProduct({ storeId: config?.storeId, customerId: CustomerId, data: payload })).unwrap().then((res) => {
            if (res) {
                // console.log("res", res)
                // setCombinedDiscounts((prevDiscounts: any) =>
                //     prevDiscounts.type == "BuyXgetY" ?
                //         prevDiscounts.filter((item: BuyXGetY) => item?.getdata?.id !== value?.getdata?.id) : prevDiscounts.filter((item: AutomaticDiscount) => item?.categoryid !== value?.categoryid)
                // );
            }
        }).catch((Error) => {
            console.log("Error in get authorized product", Error)
        })
    }

    // Method which is used to
    const updateCart = (val: string) => {
        let currentProduct = selectedOption ?? productDetails?.variantCombinationDetails[0]
        let weightCount = currentProduct?.weightCount || 0
        let stock = currentProduct?.multiLocationVariantStocks[0]?.stock
        let totalStock = stock ? stock == -1 ? Infinity : stock : 0
        if (val == 'increament') {
            let currCount = count + weightCount
            if (currCount <= totalStock) {
                setCount(((prev) => prev + weightCount))
            } else {
                ZenToast({ type: "warning", message: "Max Stock limit reached" })
            }
        }
        else if (count - weightCount > 0) {
            setCount(count - weightCount)
        }
    }

    const addToCart = async () => {
        if (selectedCustomer) {
            let currentCartProduct = cartProducts.get(VarientId)
            if (currentCartProduct?.quantity !== count || (currentCartProduct?.varientId !== selectedOption?.id && selectedOption) || currentCartProduct?.productNotes !== notes?.trim()) {
                setUpdateCartLoader(true)
                let payload: updateCartPayload = {
                    productId: params?.productId,
                    quantity: count || 1,
                    variantId: selectedOption?.id || productDetails?.variantCombinationDetails[0]?.id,
                    isUpdateCartQty: true,
                    isPos: true,
                    ...notes && { instruction: notes.trim() }
                };
                if (currentCartProduct?.cartProductId && payload?.variantId === currentCartProduct?.varientId) {
                    await dispatch(updateCartProducts({ storeId: config.storeId, cartProductId: currentCartProduct?.cartProductId!!, payload })).finally(() => {
                        setUpdateCartLoader(false)
                    })
                } else {
                    await dispatch(addCartProducts({ storeId: config.storeId, customerId: selectedCustomer?.id, payload })).finally(() => {
                        setUpdateCartLoader(false)
                    })
                }
            } else {
                ZenToast({ type: "warning", message: "You have no changes to add the cart" })
            }
        } else {
            ZenToast({ type: "warning", message: "Please select a customer before adding a product to the cart" })
        }
    }

    const isStock = useMemo(() => {
        if (selectedOption?.id) {
            return selectedOption?.multiLocationVariantStocks?.length && selectedOption?.multiLocationVariantStocks?.[0]?.stock !== 0 ? true : false
        } else
            return productDetails?.variantCombinationDetails[0]?.multiLocationVariantStocks[0]?.stock !== 0
    }, [productDetails, selectedOption])

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} enabled style={productDetailsStyle.container}>
                    <CommonHeader headerName="Product Details" backIcon={true} backIconOnpress={() => { navigation?.goBack() }} />
                    {load ?
                        <View style={productDetailsStyle.loader}>
                            <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={appColors?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
                        </View>
                        :
                        <ScrollView style={[productDetailsStyle.container, { backgroundColor: appColors.lightBackgroundColor }]}>
                            <View style={{ flex: 1, borderRadius: 10, backgroundColor: appColors?.light, marginHorizontal: 15, padding: 10, marginTop: 20, paddingBottom: 20 }}>
                                <View style={[productDetailsStyle.cardView, { flex: 1 }]}>
                                    <View style={[productDetailsStyle.imageView, { flex: width > 900 ? 0.17 : 0.25 }]}>
                                        <Image
                                            style={[productDetailsStyle.imageStyle, { width: width > 600 ? 160 : 50, height: width > 600 ? 140 : 40 }]}
                                            source={productDetails?.featuredImage ? displayImage ? { uri: config?.AWSImageUrl + displayImage } : { uri: config?.AWSImageUrl + productDetails?.featuredImage } : require('../../assets/noimage.jpg')}
                                        />
                                    </View>
                                    <View style={[{ flex: width > 900 ? 0.83 : 0.75 }]}>
                                        <ZenText content={productDetails?.name} color={appColors.lightDark} bold={true} size={fontSize?.largerFontSize} style={productDetailsStyle.productNameStyle} ellipsizeMode="tail" />
                                        {productDetails?.productOptionMappings?.[0]?.option?.optionValues[0]?.productVariantMappings[0]?.variantCombinationDetail?.sku != null ?
                                            <View style={productDetailsStyle.contentView}>
                                                <ZenText content={'SKU : ' + productDetails?.productOptionMappings[0]?.option?.optionValues[0]?.productVariantMappings[0]?.variantCombinationDetail?.sku == null} color={appColors.lightDark} size={fontSize?.smallHeaderFontSize} bold={true} />
                                            </View>
                                            : (productDetails?.variantCombinationDetails[0]?.sku != null) ?
                                                <View style={productDetailsStyle.contentView}>
                                                    <ZenText content={'SKU : ' + productDetails?.variantCombinationDetails[0]?.sku} color={appColors.lightDark} size={fontSize?.smallHeaderFontSize} bold={true} />
                                                </View> : null
                                        }
                                        <View style={productDetailsStyle.contentView}>
                                            {productDetails?.variantCombinationDetails?.length == 0 && Array.isArray(options) ?
                                                <ZenText content={CommonService.currencyStandard(currencySymbol, selectedOption?.price)} size={fontSize?.smallHeaderFontSize} bold={true} />
                                                :
                                                <ZenText content={CommonService.currencyStandard(currencySymbol, productDetails?.variantCombinationDetails[0]?.price)} size={fontSize?.smallHeaderFontSize} bold={true} />
                                            }
                                        </View>
                                    </View>
                                </View>

                                {/* <View style={[productDetailsStyle.contentView, { alignSelf: 'center' }]}>
                            {variantImage?.length > 0 &&
                                variantImage?.map((item: variantImage) => {
                                    console.log("item", JSON?.stringify(item, null, 4))
                                    return (
                                        item?.images?.length > 0 && item?.images?.map((ele: renderImageType, index: number) => {
                                            return (
                                                <Pressable key={index} style={productDetailsStyle?.variantImageView} onPress={() => { renderImage(ele) }}>
                                                    {productDetails?.isVariants == false && productDetails?.featuredImage != null && <Image source={{ uri: config?.AWSImageUrl + productDetails?.featuredImage }} style={{ width: width > 600 ? 30 : 50, height: width > 600 ? 30 : 40, marginTop: 10 }} />}
                                                    <Image
                                                        style={[productDetailsStyle.variantImageStyle, { width: width > 600 ? 30 : 50, height: width > 600 ? 30 : 40 }]}
                                                        source={ele?.fileUrl ? { uri: config?.AWSImageUrl + ele?.fileUrl } : require('../../assets/noimage.jpg')}
                                                    />
                                                </Pressable>
                                            )
                                        })
                                    )
                                })
                            }
                        </View> */}

                                <View style={[productDetailsStyle.productNameStyle, { marginLeft: 15 }]}>
                                    <View style={productDetailsStyle.contentView}>
                                        <ZenText content='Availability : ' color={appColors.lightDark} size={fontSize?.smallHeaderFontSize} />
                                        <ZenText content={isStock ? 'Many In Stock' : "Out of Stock"} color={isStock ? appColors.lightDark : appColors?.red} size={fontSize?.smallHeaderFontSize} bold={true} />
                                    </View>
                                    {options?.length > 0 &&
                                        <View>
                                            <ZenText content={productDetails?.productOptionMappings[0]?.option?.name} size={fontSize?.smallHeaderFontSize} color={appColors.primary} bold={true} style={{ marginTop: 10 }} />
                                            <View style={productDetailsStyle.checkboxView}>
                                                {options?.map((value: optionValueType) => {
                                                    return (
                                                        <ZenCheckBox
                                                            containerStyle={{ marginLeft: 20, marginTop: 10 }}
                                                            key={value?.id}
                                                            checkedColor={appColors.primary}
                                                            checkedIcon="dot-circle-o"
                                                            unCheckedIcon="circle-o"
                                                            title={value?.productVariantMappings[0]?.variantCombinationDetail?.variantName}
                                                            titleStyle={{ color: appColors?.lightDark, fontSize: fontSize?.smallHeaderFontSize, fontFamily: appFonts?.medium, marginLeft: 5 }}
                                                            checked={selectedOption ? selectedOption?.id == value?.productVariantMappings[0]?.variantCombinationDetail?.id : false}
                                                            onPress={() => { setSelectedOption(value?.productVariantMappings[0]?.variantCombinationDetail) }}
                                                        />)
                                                })}
                                            </View>
                                        </View>
                                    }
                                    {isStock &&
                                        <View style={[productDetailsStyle.cardView, { marginTop: 15 }]}>
                                            <View style={productDetailsStyle.addtoCartView}>
                                                <Pressable style={productDetailsStyle.iconView} onPress={() => updateCart('decreament')}>
                                                    <EIcon size={width > 600 ? 20 : 15} name="minus" color={appColors.grey} />
                                                </Pressable>
                                                <View style={productDetailsStyle.countTextView}>
                                                    <ZenText content={String(count)} color={appColors.primary} size={fontSize?.mediumFontSize} bold={true} style={productDetailsStyle.countTextStyle} />
                                                </View>
                                                <Pressable style={productDetailsStyle.iconView} onPress={() => updateCart('increament')}>
                                                    < EIcon size={width > 600 ? 20 : 15} name="plus" color={appColors.primary} />
                                                </Pressable>
                                            </View>
                                            <ZenButton title={cartProducts?.get(VarientId)?.varientId === (selectedOption?.id || productDetails?.variantCombinationDetails[0]?.id) ? 'Update to cart' : 'Add to Cart'}
                                                loading={updateCartLoader}
                                                onPress={() => {
                                                    if (productDetails?.productOptionMappings?.length > 0 && selectedOption || productDetails?.variantCombinationDetails?.length > 0) {
                                                        addToCart()
                                                    }
                                                    else {
                                                        Toast.show({ type: "warning", text1: "Note", text2: "Please select atleast one varient" })
                                                    }
                                                }}
                                                buttonStyle={[productDetailsStyle.buttonStyle, { marginLeft: 15 }]}
                                                titleStyle={productDetailsStyle.titleStyle} />
                                        </View>
                                    }
                                </View>
                            </View>
                            <View style={productDetailsStyle.container}>
                                {combinedDiscounts?.length > 0 &&
                                    <View style={productDetailsStyle.container}>
                                        <ZenText content='Available offers' color={appColors.primary} size={fontSize?.largerFontSize} bold={true} style={productDetailsStyle.offerTextStyle} />
                                        {combinedDiscounts?.map((item: any, index: number) => {
                                            return (
                                                <View key={index} style={{ borderRadius: 10, backgroundColor: appColors?.light, marginHorizontal: 15, padding: 15, marginTop: 15 }}>
                                                    <View style={productDetailsStyle.cardView}>
                                                        {item?.type == "BuyXgetY" &&
                                                            <View style={productDetailsStyle.cardView}>
                                                                <View style={productDetailsStyle.cardView}>
                                                                    <Image style={{ width: (width > 900 || width > 600) ? 70 : 30, height: (width > 900 || width > 600) ? 60 : 30 }} source={item?.buydata[0]?.featuredImage ? { uri: config.AWSImageUrl + item?.buydata[0]?.featuredImage } : require('../../assets/noimage.jpg')} />
                                                                    < EIcon size={width > 600 ? 20 : 15} name="plus" color={appColors.primary} style={productDetailsStyle.plusIconStyle} />
                                                                    <Image style={{ width: (width > 900 || width > 600) ? 70 : 30, height: (width > 900 || width > 600) ? 60 : 30 }} source={item?.getdata?.featuredImage ? { uri: config.AWSImageUrl + item?.getdata?.featuredImage } : require('../../assets/noimage.jpg')} />
                                                                </View>
                                                                <View style={productDetailsStyle.discountView}>
                                                                    <View style={[productDetailsStyle.cardView, productDetailsStyle.container]}>
                                                                        <ZenText content={item?.getdata?.productName} size={fontSize?.smallerFontSize} bold={true} color={appColors.lightDark} />
                                                                        <ZenBadge badgeName={item?.type} badgeColor={'#ffedbd'} badgeTextColor={appColors?.lightDark} badgeContainerStyle={[productDetailsStyle.badgeStyle, { height: width > 600 ? 25 : 30, width: width > 600 ? undefined : 170 }]} badgeTextStyle={productDetailsStyle.textStyle} />
                                                                    </View>
                                                                    <ZenText content={item?.getdata?.price + " OFF on purchase of minimum quantity of "} size={fontSize.smallFontSize} color={appColors?.grey} />
                                                                </View>
                                                            </View>
                                                        }
                                                        {item?.type == "Automatic" &&
                                                            <View style={productDetailsStyle.cardView}>
                                                                <Image style={{ width: (width > 900 || width > 600) ? 70 : 30, height: (width > 900 || width > 600) ? 60 : 30 }} source={require('../../assets/noimage.jpg')}
                                                                />
                                                                <View style={productDetailsStyle.discountView}>
                                                                    <View style={[productDetailsStyle.cardView, productDetailsStyle.container]}>
                                                                        <ZenText content={item?.title} size={fontSize?.smallerFontSize} bold={true} color={appColors.lightDark} />
                                                                        <ZenBadge badgeName={item?.type} badgeColor={'#ffedbd'} badgeTextColor={appColors?.lightDark} badgeContainerStyle={[productDetailsStyle.badgeStyle]} badgeTextStyle={productDetailsStyle.textStyle} />
                                                                    </View>
                                                                    <ZenText content={(item?.discounttypecode == "FIXED" ? "$" : "") + (parseFloat(item?.discountvalue)) + (item?.discounttypecode == "PERCENTAGE" ? "% OFF on purchase of this product" : " OFF on purchase of this product")} size={fontSize.smallFontSize} color={appColors?.grey} />
                                                                </View>
                                                            </View>
                                                        }
                                                        {/* <View style={productDetailsStyle.buttonView}>
                                                    <ZenButton title='Claim'
                                                        onPress={() => { claimOffer(item) }}
                                                        buttonStyle={productDetailsStyle.buttonStyle}
                                                        titleStyle={productDetailsStyle.titleStyle} />
                                                </View> */}
                                                    </View>
                                                </View>
                                            )
                                        })}
                                    </View>}
                            </View>
                            <View style={{ borderRadius: 10, backgroundColor: appColors?.light, marginHorizontal: 15, padding: 15, marginTop: 20, marginBottom: 20 }}>
                                <View>
                                    {/* <ZenText content='Tax' color={appColors.primary} size={fontSize?.largerFontSize} bold={true} style={productDetailsStyle.taxStyle} />
                            <View style={productDetailsStyle.gstView}>
                                <ZenText content='GST 18%' color={appColors.lightDark} size={fontSize?.smallHeaderFontSize} bold={true} style={[productDetailsStyle.productNameStyle, productDetailsStyle.taxStyle]} />
                                <ZenSwitch
                                    value={isActive}
                                    onValueChange={() => { setIsActive(!isActive) }}
                                    style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }], }}
                                    activeColor={appColors?.primary}
                                    activeThumbColor={appColors?.light}
                                    inActiveColor={appColors.borderColor}
                                    inactiveThumbColor={appColors?.light} />
                            </View> */}
                                    <ZenInput
                                        label="Add Product Notes"
                                        value={notes}
                                        labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                        inputStyle={theme?.modifierSetStyle?.inputStyle}
                                        type="outlined"
                                        containerStyle={{ paddingHorizontal: 15 }}
                                        inputContainerStyle={{ paddingHorizontal: 8, height: 90 }}
                                        onChangeText={(text) => setNotes(text)}
                                        multiline={true}
                                    />
                                </View>
                            </View>
                        </ScrollView>
                    }
                </KeyboardAvoidingView >
            )}
        </ThemeConsumer>
    )
}

export default ProductDetails;